import { defaultSoleInformations } from '@/domains/sole/services/utils/defaultValues';
import { defaultOfficeUser } from '@/domains/office/utils/defaultValues';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type SolePresetInterface from '@/domains/solePreset/typescript/interfaces/SolePresetInterface';
import type SoleParametersFileInterface from '@/domains/sole/typescript/interfaces/SoleParametersFileInterface';

export default defineStore('solePreset', () => {
    const currentPreset = ref<SolePresetInterface>({
        name: '',
        officeUser: defaultOfficeUser,
        ...defaultSoleInformations,
    });

    const solePresets = ref<SolePresetInterface[]>([]);

    // Last-known-good parameters loaded for the current preset. Reused as the capture
    // fallback when the preset save races an un-ready sole (ADR 0002). Lives here rather
    // than in the sole store so soleStore.$reset() on preset load does not wipe it.
    const lastPresetParameters = ref<SoleParametersFileInterface>();

    const selectSolePresetById = (solePresetId?: string): void => {
        if (!solePresetId) {
            return;
        }
        currentPreset.value = solePresets.value.find((solePreset) => solePreset.id === solePresetId) || {
            name: '',
            officeUser: defaultOfficeUser,
            ...defaultSoleInformations,
        };
    };

    function isSolePresetSelected(solePreset: SolePresetInterface): boolean {
        return currentPreset.value?.id === solePreset.id;
    }

    function unselectSolePreset(): void {
        currentPreset.value = {
            name: '',
            officeUser: defaultOfficeUser,
            ...defaultSoleInformations,
        };
    }

    // Setup stores get no automatic $reset(): without this one, every resetStores([SOLE_PRESET])
    // was a silent no-op in production and threw in development.
    function $reset(): void {
        console.info('reset sole preset store');
        unselectSolePreset();
        solePresets.value = [];
        lastPresetParameters.value = undefined;
    }

    return {
        currentPreset,
        solePresets,
        lastPresetParameters,
        isSolePresetSelected,
        unselectSolePreset,
        selectSolePresetById,
        $reset,
    };
});
